import React, { createContext, useContext, useState } from 'react';

interface CookieConsentState {
  analytics: boolean;
  ads: boolean;
  hasDecided: boolean;
}

interface CookieConsentContextType extends CookieConsentState {
  acceptAll: () => void;
  declineAll: () => void;
}

const STORAGE_KEY = 'cookie_consent';
const CONSENT_MAX_AGE_MS = 13 * 30 * 24 * 60 * 60 * 1000; // ~13 months (CNIL)

const CookieConsentContext = createContext<
  CookieConsentContextType | undefined
>(undefined);

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error(
      'useCookieConsent must be used within a CookieConsentProvider'
    );
  }
  return context;
};

const updateGtagConsent = (granted: boolean) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function')
    return;
  const value = granted ? 'granted' : 'denied';
  window.gtag('consent', 'update', {
    analytics_storage: value,
    ad_storage: value,
    ad_user_data: value,
    ad_personalization: value,
  });
};

interface StoredConsent {
  analytics: boolean;
  ads: boolean;
  timestamp: number;
}

const DEFAULT_STATE: CookieConsentState = {
  analytics: false,
  ads: false,
  hasDecided: false,
};

const loadConsent = (): CookieConsentState => {
  if (typeof window === 'undefined') return DEFAULT_STATE;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_STATE;
    const stored: StoredConsent = JSON.parse(raw);

    // CNIL: consent expires after ~13 months
    if (Date.now() - stored.timestamp > CONSENT_MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY);
      return DEFAULT_STATE;
    }

    return { analytics: stored.analytics, ads: stored.ads, hasDecided: true };
  } catch {
    return DEFAULT_STATE;
  }
};

const saveConsent = (analytics: boolean, ads: boolean) => {
  try {
    const data: StoredConsent = { analytics, ads, timestamp: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // localStorage unavailable (private browsing, quota exceeded)
    // Consent still applies for this session via React state
  }
};

// Replay saved consent synchronously at module evaluation time so the
// gtag('consent', 'update') lands well within the wait_for_update window.
// A useEffect would add ~200-500ms of React hydration delay on slow devices.
const _initialConsent = loadConsent();
if (_initialConsent.hasDecided && _initialConsent.analytics) {
  updateGtagConsent(true);
}

interface CookieConsentProviderProps {
  children: React.ReactNode;
}

export const CookieConsentProvider: React.FC<CookieConsentProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<CookieConsentState>(loadConsent);

  const acceptAll = () => {
    setState({ analytics: true, ads: true, hasDecided: true });
    saveConsent(true, true);
    updateGtagConsent(true);
  };

  const declineAll = () => {
    setState({ analytics: false, ads: false, hasDecided: true });
    saveConsent(false, false);
    updateGtagConsent(false);
  };

  return (
    <CookieConsentContext.Provider value={{ ...state, acceptAll, declineAll }}>
      {children}
    </CookieConsentContext.Provider>
  );
};
